import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { useBeta } from '../../context/BetaContext';

// Check if registration is disabled via environment variable
const isRegistrationDisabled = import.meta.env.VITE_DISABLE_REGISTRATION === 'true';

const Footer = () => {
    const currentYear = new Date().getFullYear();
    const { t } = useTranslation();
    const { betaEnabled, betaLoading } = useBeta();

    // Pendant la beta on masque les liens marketing (Decouvrir, Tarifs).
    // Les liens legaux restent accessibles : ils sont hors de cette garde.
    const showMarketing = !isRegistrationDisabled && !betaLoading && !betaEnabled;

    // Hide entire footer if registration is disabled
    if (isRegistrationDisabled) {
        return null;
    }

    return (
        <footer className="bg-linear-to-r from-cabaza-purple to-cabaza-pink text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    {/* About Section */}
                    <div>
                        <h3 className="text-lg font-bold mb-4">{t.footer?.about || 'About'}</h3>
                        <p className="text-white/80 text-sm">
                            {t.footer?.aboutText || 'Cabaza is your digital business card solution to share your professional information with ease.'}
                        </p>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h3 className="text-lg font-bold mb-4">{t.footer?.quickLinks || 'Quick Links'}</h3>
                        <ul className="space-y-2">
                            <li>
                                <Link to="/" className="text-white/80 hover:text-white text-sm transition-colors inline-flex items-center min-h-11 sm:min-h-0 ">
                                    {t.nav?.home || 'Home'}
                                </Link>
                            </li>
                            {showMarketing && (
                                <>
                                    <li>
                                        <Link to="/discover" className="text-white/80 hover:text-white text-sm transition-colors inline-flex items-center min-h-11 sm:min-h-0 ">
                                            {t.nav?.discover || 'Discover Cabaza'}
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/pricing" className="text-white/80 hover:text-white text-sm transition-colors inline-flex items-center min-h-11 sm:min-h-0 ">
                                            {t.nav?.pricing || 'Pricing'}
                                        </Link>
                                    </li>
                                </>
                            )}
                            <li>
                                <Link to="/login" className="text-white/80 hover:text-white text-sm transition-colors inline-flex items-center min-h-11 sm:min-h-0 ">
                                    {t.nav?.login || 'Login'}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Legal */}
                    <div>
                        <h3 className="text-lg font-bold mb-4">{t.footer?.legal || 'Legal'}</h3>
                        <ul className="space-y-2">
                            <li>
                                <Link to="/legal" className="text-white/80 hover:text-white text-sm transition-colors inline-flex items-center min-h-11 sm:min-h-0 ">
                                    {t.footer?.legalNotice || 'Legal notice'}
                                </Link>
                            </li>
                            <li>
                                <Link to="/privacy" className="text-white/80 hover:text-white text-sm transition-colors inline-flex items-center min-h-11 sm:min-h-0 ">
                                    {t.footer?.privacy || 'Privacy policy'}
                                </Link>
                            </li>
                            <li>
                                <Link to="/terms" className="text-white/80 hover:text-white text-sm transition-colors inline-flex items-center min-h-11 sm:min-h-0 ">
                                    {t.footer?.terms || 'Terms of use'}
                                </Link>
                            </li>
                            <li>
                                <Link to="/cookies" className="text-white/80 hover:text-white text-sm transition-colors inline-flex items-center min-h-11 sm:min-h-0 ">
                                    {t.footer?.cookies || 'Cookie policy'}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Contact */}
                    <div>
                        <h3 className="text-lg font-bold mb-4">{t.footer?.contact || 'Contact'}</h3>
                        <ul className="space-y-3">
                            <li className="flex items-start gap-2 text-sm text-white/80">
                                <Mail className="w-4 h-4 mt-0.5 shrink-0" />
                                <a href="mailto:info@cabaza.be" className="hover:text-white transition-colors inline-flex items-center min-h-11 sm:min-h-0 ">
                                    info@cabaza.be
                                </a>
                            </li>
                            <li className="flex items-start gap-2 text-sm text-white/80">
                                <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                                <span>Rue de Maréchal de Saint-André, Mariembourg</span>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="mt-8 pt-8 border-t border-white/20">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="text-sm text-center md:text-left text-white/80">
                            <p className="mb-1">
                                {t.footer?.productOf || 'Cabaza is a product of Didier Nactergal IT Consultant'}
                            </p>
                            <p>
                                © {currentYear} Cabaza. {t.footer?.rights || 'All rights reserved.'}
                            </p>
                        </div>
                        <div className="flex gap-6">
                            <a
                                href="https://facebook.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-white/80 hover:text-white transition-colors inline-flex items-center justify-center min-w-11 min-h-11 sm:min-w-0 sm:min-h-0"
                            >
                                Facebook
                            </a>
                            <a
                                href="https://twitter.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-white/80 hover:text-white transition-colors inline-flex items-center justify-center min-w-11 min-h-11 sm:min-w-0 sm:min-h-0"
                            >
                                Twitter
                            </a>
                            <a
                                href="https://linkedin.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-white/80 hover:text-white transition-colors inline-flex items-center justify-center min-w-11 min-h-11 sm:min-w-0 sm:min-h-0"
                            >
                                LinkedIn
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
